import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { sendNotification } from '@/lib/notifications/send-notification';

export type AssignExerciseInput = {
  studentId: string;
  exerciseId: string;
  dueAt?: string | null;
  note?: string | null;
};

export function useAssignExercise() {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ studentId, exerciseId, dueAt, note }: AssignExerciseInput) => {
      const tutorId = user?.id;
      if (!tutorId) throw new Error('Non authentifié');

      // Un assignement INDIVIDUEL (class_id null) existe-t-il déjà pour ce trio ?
      // La contrainte partielle `assignments_individual_unique` interdit un doublon —
      // ré-assigner un exercice déjà donné (souvent parce qu'il est déjà terminé)
      // doit donc repartir d'un devoir « à faire » propre, pas remonter une erreur SQL.
      const { data: existing, error: findErr } = await supabase
        .from('assignments')
        .select('id')
        .eq('tutor_id', tutorId)
        .eq('student_id', studentId)
        .eq('exercise_id', exerciseId)
        .is('class_id', null)
        .maybeSingle();

      if (findErr) throw new Error(findErr.message);

      if (existing) {
        const { error } = await supabase
          .from('assignments')
          .update({
            due_at: dueAt ?? null,
            note: note ?? null,
            status: 'pending',
            started_at: null,
            completed_at: null,
          })
          .eq('id', existing.id);
        if (error) throw new Error(error.message);
      } else {
        const { error } = await supabase.from('assignments').insert({
          tutor_id: tutorId,
          student_id: studentId,
          exercise_id: exerciseId,
          due_at: dueAt ?? null,
          note: note ?? null,
          status: 'pending',
        });
        if (error) throw new Error(error.message);
      }

      return { studentId, exerciseId };
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['tutor-student-detail', variables.studentId] });
      sendNotification({
        type: 'exercise_assigned',
        student_id: variables.studentId,
        exercise_id: variables.exerciseId,
      });
    },
  });
}

export type AssignToClassInput = {
  classId: string;
  exerciseId: string;
  dueAt?: string | null;
  note?: string | null;
};

export function useAssignExerciseToClass() {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ classId, exerciseId, dueAt, note }: AssignToClassInput) => {
      const tutorId = user?.id;
      if (!tutorId) throw new Error('Non authentifié');

      // Get active class members
      const { data: members, error: membersErr } = await supabase
        .from('class_members')
        .select('student_id')
        .eq('class_id', classId)
        .eq('status', 'active');

      if (membersErr) throw new Error(membersErr.message);
      if (!members || members.length === 0) throw new Error('Aucun élève actif dans cette classe');

      // Fan-out: one assignment per student
      const assignments = members.map((m: { student_id: string }) => ({
        tutor_id: tutorId,
        student_id: m.student_id,
        exercise_id: exerciseId,
        class_id: classId,
        due_at: dueAt ?? null,
        note: note ?? null,
        status: 'pending' as const,
      }));

      const { error } = await supabase.from('assignments').insert(assignments);
      if (error) throw new Error(error.message);

      return {
        count: members.length,
        studentIds: members.map((m: { student_id: string }) => m.student_id),
        exerciseId,
      };
    },
    onSuccess: (data, variables) => {
      qc.invalidateQueries({ queryKey: ['tutor-class-detail', variables.classId] });
      data?.studentIds.forEach((studentId: string) =>
        sendNotification({
          type: 'exercise_assigned',
          student_id: studentId,
          exercise_id: variables.exerciseId,
        }),
      );
    },
  });
}

export function useDeleteAssignment() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      assignmentId,
      studentId,
    }: {
      assignmentId: string;
      studentId?: string;
    }) => {
      const { error } = await supabase.from('assignments').delete().eq('id', assignmentId);
      if (error) throw new Error(error.message);
      return { studentId };
    },
    onSuccess: (result) => {
      if (result.studentId) {
        qc.invalidateQueries({ queryKey: ['tutor-student-detail', result.studentId] });
      }
    },
  });
}
